import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Container,
  useToast,
  Heading,
  Text,
  Badge,
  Center,
  Spinner,
  Select
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { RecaptchaVerifier, linkWithPhoneNumber } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, firestore, functions, doc, updateDoc, getDoc } from "../../firebase/firebaseConfig";
import { fetchUserProfile, awardFreeTradesAfterVerification } from "../../services/userService";
import { sanitizePhoneNumber } from "../../utils/formatters";
import { useAuth } from "../../context/AuthProvider";
import { PeerxLogo } from "../../components/icons/Icons";
import { useTranslation } from "react-i18next";


const MotionButton = motion(Button);

const VerificationPage = () => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const { user, firebaseUser } = useAuth();
  const toast = useToast();

  const [formData, setFormData] = useState({
    email: "",
    phoneNumber: "",
    emailCode: "",
    phoneCode: "",
  });

  const [loading, setLoading] = useState(true);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [resendEmailTimer, setResendEmailTimer] = useState(60);  // 60 seconds initial timer
  const [resendSMSTimer, setResendSMSTimer] = useState(0);

    // Language Switcher
    const handleLanguageChange = (event) => {
      i18n.changeLanguage(event.target.value);
    };

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const fetchedUser = await fetchUserProfile(user.uid);
        const sanitizedPhone = fetchedUser.phoneNumber ? sanitizePhoneNumber(fetchedUser.phoneNumber) : "";

        setFormData({
          email: user.email || "",
          phoneNumber: sanitizedPhone || "",
          phoneCode: "",
        });

        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setEmailVerified(!!userData.emailVerified);
          setPhoneVerified(!!userData.phoneVerified);
        }
      } catch (error) {
        toast({
          title: t("verificationpage.error_title"),
          description: t("verificationpage.error_user_profile"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, toast]);

  // Redirect when both email and phone are verified
  useEffect(() => {
    if (emailVerified && phoneVerified) {
      navigate("/personal/dashboard");
    }
  }, [emailVerified, phoneVerified, navigate]);

  useEffect(() => {
    let timerInterval;

    if (resendEmailTimer > 0) {
      timerInterval = setInterval(() => {
        setResendEmailTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerInterval);  // Clear the timer when it reaches zero
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timerInterval);  // Cleanup on unmount
  }, [resendEmailTimer]);

  useEffect(() => {
    let timerInterval;

    if (resendSMSTimer > 0) {
      timerInterval = setInterval(() => {
        setResendSMSTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerInterval);  // Clear the timer when it reaches zero
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timerInterval);  // Cleanup on unmount
  }, [resendSMSTimer]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const sendEmailVerification = async () => {
    if (resendEmailTimer > 0) return;  // Prevent re-sending while the timer is running

    if (!formData.email) {
      toast({ title: t("verificationpage.error_title"), description: t("verificationpage.error_email_required"), status: "error" });
      return;
    }

    setVerifyingEmail(true);
    try {
      const sendEmail = httpsCallable(functions, "sendEmailVerificationCode");
      await sendEmail({ uid: user.uid, email: formData.email });
      toast({ title: t("verificationpage.email_verification_sent"), status: "success" });
      setResendEmailTimer(60);  // Reset the timer to 60 seconds
    } catch (error) {
      toast({
        title: t("verificationpage.error_title"),
        description: error.message || t("verificationpage.error_email_failed"),
        status: "error",
      });
    } finally {
      setVerifyingEmail(false);
    }
  };

  const verifyEmailCode = async () => {
    if (!formData.emailCode.trim()) {
      toast({ title: t("verificationpage.error_title"), description: t("verificationpage.error_email_code_required"), status: "error" });
      return;
    }

    setVerifyingEmail(true);
    try {
      const checkCode = httpsCallable(functions, "checkEmailVerificationCode");
      await checkCode({ uid: user.uid, verificationCode: formData.emailCode.trim() });
      toast({ title: t("verificationpage.email_verified"), status: "success" });
      setEmailVerified(true);
      await updateDoc(doc(firestore, "users", user.uid), { emailVerified: true, verifiedEmail: formData.email });
      
      // Check if all conditions are met to award free trades
      await awardFreeTradesAfterVerification(user.uid);

      toast({ title: "Email Verified", status: "success" });
    } catch (error) {
      toast({
        title: t("verificationpage.error_title"),
        description: error.message || t("verificationpage.error_verifying_email"),
        status: "error",
      });
    } finally {
      setVerifyingEmail(false);
    }
  };

  // Updated phone verification function
  const sendPhoneVerification = async () => {
    if (!firebaseUser) {
      toast({ title: t("verificationpage.error_title"), description: t("verificationpage.error_phone_required"), status: "error" });
      return;
    }
  
    setVerifyingPhone(true);
  
    try {
      // Ensure Firebase Auth User Exists
      if (!auth.currentUser) {
        throw new Error(t("verificationpage.error_auth_required"));
      }
  
      // Ensure reCAPTCHA Container Exists in DOM
      if (!document.getElementById("recaptcha-container")) {
        throw new Error(t("verificationpage.error_recaptcha_missing"));
      }
  
      // Initialize reCAPTCHA only once
      if (!window.recaptchaVerifier) {
        console.log("🔹 Initializing reCAPTCHA...");
        window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible", // Can be "normal" for visible reCAPTCHA
          callback: (response) => {
            console.log("✅ reCAPTCHA resolved:", response);
          },
          'expired-callback': () => {
            console.warn("⚠️ reCAPTCHA expired. Resetting...");
            window.recaptchaVerifier = null; // Reset reCAPTCHA
          },
        });
      }
  
      // Render reCAPTCHA (ensure it runs before sending verification)
      await window.recaptchaVerifier.render();
  
      console.log("📞 Sending verification code to:", formData.phoneNumber);
  
      // Link phone number to existing user
      const result = await linkWithPhoneNumber(firebaseUser, formData.phoneNumber, window.recaptchaVerifier);
  
      setConfirmationResult(result);
      toast({ title: t("verificationpage.sms_sent"), status: "success" });
      setResendSMSTimer(60);
    } catch (error) {
      console.error("❌ Error sending phone verification:", error);
      toast({
        title: t("verificationpage.error_title"),
        description: error.message || t("verificationpage.error_sms_failed"),
        status: "error",
      });
    } finally {
      setVerifyingPhone(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (!formData.phoneCode.trim()) {
      toast({ title: t("verificationpage.error_title"), description: t("verificationpage.error_phone_code_required"), status: "error" });
      return;
    }

    if (!confirmationResult) {
      toast({ title: t("verificationpage.error_title"), description: t("verificationpage.error_request_new_code"), status: "error" });
      return;
    }

    setVerifyingPhone(true);
    try {
      // Confirm the phone code
      await confirmationResult.confirm(formData.phoneCode.trim());
      toast({ title: t("verificationpage.phone_verified"), status: "success" });
      setPhoneVerified(true);

      // Update the user's profile in Firestore
      await updateDoc(doc(firestore, "users", user.uid), { phoneVerified: true, verifiedPhoneNumber: formData.phoneNumber });
      
      // Check if all conditions are met to award free trades
      await awardFreeTradesAfterVerification(user.uid);

      toast({ title: "Phone Verified", status: "success" });
    } catch (error) {
      toast({
        title: t("verificationpage.error_title"),
        description: error.message || t("verificationpage.error_verifying_phone"),
        status: "error",
      });
    } finally {
      setVerifyingPhone(false);
    }
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <Box py={10} bg="brand.300" minH="100vh">
            {/* Language Switcher */}
            <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
              <Select
                value={i18n.language}
                onChange={handleLanguageChange}
                width="120px"
                bg="white"
                color="black"
                size="sm"
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="es">Español</option>
              </Select>
            </Box>
      <Container maxW="md">
        {/* Logo */}
        <Heading mb="20px" size="2xl" fontFamily="Poppins" color="brand.200" fontWeight="bold" letterSpacing="wider" as={RouterLink} to="/">
          <PeerxLogo h="90px" w="160px" />
        </Heading>
        <Box as={motion.div} mt="20px" p={8} bg="brand.100" borderRadius="lg" shadow="lg">
          <VStack spacing={6}>
            <Heading size="xl" color="brand.400">
            {t("verificationpage.verify_email_phone")}
            </Heading>
            <div id="recaptcha-container" style={{ minHeight: "50px" }}></div>

            {emailVerified && <Badge colorScheme="green">{t("verificationpage.email_verified_badge")}</Badge>}
            {phoneVerified && <Badge colorScheme="green">{t("verificationpage.phone_verified_badge")}</Badge>}

            <FormControl>
            <FormLabel fontSize="lg" color="brand.400">{t("verificationpage.email_verification_label")}</FormLabel>
            <Input
                name="emailCode"
                placeholder={t("verificationpage.email_verification_placeholder")}
                value={formData.emailCode}
                onChange={handleChange}
              />
              <Button mt={2} onClick={verifyEmailCode} isLoading={verifyingEmail}>
              {t("verificationpage.verify_email_button")}
              </Button>
            </FormControl>

            {!showEmailInput ? (
              <Text color="blue.500" onClick={() => setShowEmailInput(true)}>{t("verificationpage.change_email_link")}</Text>
            ) : (
              <FormControl>
                <FormLabel fontSize="lg" color="brand.400">{t("verificationpage.email_label")}</FormLabel>
                <Input
                  name="email"
                  type="email"
                  placeholder={t("verificationpage.email_placeholder")}
                  value={formData.email}
                  onChange={handleChange}
                />
                <Button
                  mt={2}
                  onClick={sendEmailVerification}
                  isLoading={verifyingEmail}
                  isDisabled={resendEmailTimer > 0}
                >
                  {resendEmailTimer > 0 ? `${t("verificationpage.resend_in")} ${resendEmailTimer}s` : t("verificationpage.send_email_verification_button")}
                  </Button>
              </FormControl>
            )}

            <FormControl>
            <FormLabel fontSize="lg" color="brand.400">{t("verificationpage.phone_verification_label")}</FormLabel>
            <Input
                name="phoneCode"
                placeholder={t("verificationpage.phone_verification_placeholder")}
                value={formData.phoneCode}
                onChange={handleChange}
              />
              <Button mt={2} onClick={verifyPhoneCode} isLoading={verifyingPhone}>
              {t("verificationpage.verify_phone_button")}
              </Button>
            </FormControl>

            {!showPhoneInput ? (
              <Text color="blue.500" onClick={() => setShowPhoneInput(true)}>{t("verificationpage.change_phone_link")}</Text>
            ) : (
              <FormControl>
                <FormLabel fontSize="lg" color="brand.400">{t("verificationpage.phone_label")}</FormLabel>
                <Input
                  type="tel"
                  name="phoneNumber"
                  placeholder={t("verificationpage.phone_placeholder")}
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
                <Button
                  mt={2}
                  onClick={sendPhoneVerification}
                  isLoading={verifyingPhone}
                  isDisabled={resendSMSTimer > 0 || !formData.phoneNumber}
                >
                  {resendSMSTimer > 0 ? `${t("verificationpage.resend_in")} ${resendSMSTimer}s` : t("verificationpage.send_sms_verification_button")}
                  </Button>
              </FormControl>
            )}
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default VerificationPage;
